import React, { useMemo } from 'react';
import {
    X,
    Lock,
    LockOpen,
    Trash,
    MapPin,
    User,
    Package,
    Clock,
    ArrowsOutLineVertical,
    Copy,
    ArrowUp,
    ArrowDown
} from '@phosphor-icons/react';
import { useIdea } from '../../context/IdeaContext';
import { IdeaCard, CardInstance, AttachedEntity, CardFocus } from '../../types';
import { EntityChip } from './EntityChip';

// Focus labels
const FOCUS_LABELS: Record<CardFocus, string> = {
    note: 'Note',
    scene: 'Scene',
    character: 'Character',
    prop: 'Prop',
    prop_significance: 'Prop Significance',
    relationship: 'Relationship'
};

interface CardInspectorProps {
    card: IdeaCard;
    isEmbedded?: boolean;
}

export const CardInspector: React.FC<CardInspectorProps> = ({ card, isEmbedded = false }) => {
    const {
        workspaceState,
        updateCard,
        deleteCard,
        detachEntity,
        placeCard,
        updateInstance,
        removeInstance,
        selectCard,
        selectInstance
    } = useIdea();

    // Get selected instance (if any)
    const selectedInstance = useMemo(() => {
        if (!workspaceState.selectedInstanceId || !card.instances) return null;
        return card.instances.find(i => i.id === workspaceState.selectedInstanceId) || null;
    }, [card.instances, workspaceState.selectedInstanceId]);

    // Check if card is placed
    const isPlaced = card.instances && card.instances.length > 0;
    const instanceCount = card.instances?.length || 0;

    // Premiere Pro style inspector
    return (
        <div className={`flex flex-col h-full ${isEmbedded ? '' : 'border-b border-iron/40'}`}>
            {/* Header - Premiere panel header */}
            <div className={`flex items-center justify-between px-3 py-2 border-b border-iron/40 ${isEmbedded ? 'bg-neutral-900' : 'bg-zinc-900'}`}>
                <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                    {isEmbedded ? 'Inspector' : 'Card Inspector'}
                </span>
                {!isEmbedded && (
                    <button
                        onClick={() => selectCard(null)}
                        className="p-1 rounded text-gray-500 hover:text-gray-400 hover:bg-graphite transition-colors"
                    >
                        <X className="w-3 h-3" />
                    </button>
                )}
            </div>

            {/* Content */}
            <div className={`p-3 space-y-4 ${isEmbedded ? 'flex-1 overflow-y-auto custom-scrollbar' : ''}`}>
                {/* Focus Type & Lock */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">
                            Focus Type
                        </span>
                        <button
                            onClick={() => updateCard(card.id, { focus_locked: !card.focus_locked })}
                            className={`
                                flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px]
                                transition-colors
                                ${card.focus_locked
                                    ? 'text-thing bg-thing/10'
                                    : 'text-gray-500 hover:text-gray-400'
                                }
                            `}
                            title={card.focus_locked ? 'Unlock focus inference' : 'Lock focus'}
                        >
                            {card.focus_locked ? (
                                <>
                                    <Lock className="w-3 h-3" />
                                    Locked
                                </>
                            ) : (
                                <>
                                    <LockOpen className="w-3 h-3" />
                                    Auto
                                </>
                            )}
                        </button>
                    </div>
                    <div className="px-2 py-1.5 rounded bg-onyx border border-iron/40 text-xs text-neutral-200">
                        {FOCUS_LABELS[card.focus]}
                    </div>
                </div>

                {/* Attached Entities */}
                <div className="space-y-2">
                    <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">
                        Attached Entities ({card.entities.length})
                    </span>
                    {card.entities.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                            {card.entities.map(entity => (
                                <EntityChip
                                    key={entity.id}
                                    entity={entity}
                                    size="md"
                                    onRemove={() => detachEntity(entity.id, card.id)}
                                />
                            ))}
                        </div>
                    ) : (
                        <p className="text-[10px] text-gray-500 italic">
                            Drag characters, locations, or props here
                        </p>
                    )}
                </div>

                {/* Timeline Placements */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">
                            Timeline ({instanceCount})
                        </span>
                        {!isPlaced && (
                            <span className="text-[9px] text-thing/70 italic">
                                Not placed
                            </span>
                        )}
                    </div>

                    {/* Instance List */}
                    {card.instances && card.instances.length > 0 ? (
                        <div className="space-y-1">
                            {card.instances.map((instance, idx) => (
                                <InstanceRow
                                    key={instance.id}
                                    instance={instance}
                                    index={idx}
                                    isSelected={workspaceState.selectedInstanceId === instance.id}
                                    onSelect={() => selectInstance(instance.id)}
                                    onUpdate={(updates) => updateInstance(instance.id, updates)}
                                    onRemove={() => removeInstance(instance.id)}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-3">
                            <p className="text-[10px] text-gray-500 mb-2">
                                Drag this card to the timeline to place it
                            </p>
                            <div className="flex items-center justify-center gap-2">
                                <QuickPlaceButton
                                    label="Macro"
                                    onClick={() => placeCard(card.id, 'macro', 0.5, 0.1)}
                                />
                                <QuickPlaceButton
                                    label="Micro"
                                    onClick={() => placeCard(card.id, 'micro', 0.5)}
                                />
                            </div>
                        </div>
                    )}

                    {/* Add Another Placement */}
                    {isPlaced && instanceCount < 5 && (
                        <button
                            onClick={() => placeCard(card.id, 'micro', 0.5)}
                            className="w-full flex items-center justify-center gap-1 px-2 py-1.5 rounded border border-dashed border-iron text-[10px] text-gray-500 hover:text-gray-400 hover:border-iron/80 transition-colors"
                        >
                            <Copy className="w-3 h-3" />
                            Add another appearance
                        </button>
                    )}
                </div>

                {/* Selected Instance Controls */}
                {selectedInstance && (
                    <div className="space-y-2 pt-2 border-t border-iron/40">
                        <span className="text-[10px] text-slate-blue font-medium uppercase tracking-wider">
                            Selected Appearance
                        </span>

                        {/* Position Slider */}
                        <div className="space-y-1">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] text-gray-500">Position</span>
                                <span className="text-[10px] text-gray-400">
                                    {Math.round(selectedInstance.position * 100)}%
                                </span>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={selectedInstance.position * 100}
                                onChange={(e) => updateInstance(selectedInstance.id, {
                                    position: parseInt(e.target.value) / 100
                                })}
                                className="w-full h-1 bg-graphite rounded appearance-none cursor-pointer accent-slate-blue"
                            />
                        </div>

                        {/* Duration (Macro only) */}
                        {selectedInstance.lane === 'macro' && (
                            <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] text-gray-500">Duration</span>
                                    <span className="text-[10px] text-gray-400">
                                        {Math.round((selectedInstance.duration || 0.1) * 100)}%
                                    </span>
                                </div>
                                <input
                                    type="range"
                                    min="5"
                                    max="50"
                                    value={(selectedInstance.duration || 0.1) * 100}
                                    onChange={(e) => updateInstance(selectedInstance.id, {
                                        duration: parseInt(e.target.value) / 100
                                    })}
                                    className="w-full h-1 bg-graphite rounded appearance-none cursor-pointer accent-slate-blue"
                                />
                            </div>
                        )}

                        {/* Move to other lane */}
                        <button
                            onClick={() => updateInstance(selectedInstance.id, {
                                lane: selectedInstance.lane === 'macro' ? 'micro' : 'macro'
                            })}
                            className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded bg-graphite border border-iron/40 text-[10px] text-gray-400 hover:text-neutral-200 transition-colors"
                        >
                            <ArrowsOutLineVertical className="w-3 h-3" />
                            Move to {selectedInstance.lane === 'macro' ? 'Micro' : 'Macro'}
                        </button>
                    </div>
                )}

                {/* Delete Card */}
                <button
                    onClick={() => {
                        if (confirm('Delete this card and all its placements?')) {
                            deleteCard(card.id);
                        }
                    }}
                    className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-[10px] text-red-400/70 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                >
                    <Trash className="w-3 h-3" />
                    Delete Card
                </button>
            </div>
        </div>
    );
};

// Instance Row Component
interface InstanceRowProps {
    instance: CardInstance;
    index: number;
    isSelected: boolean;
    onSelect: () => void;
    onUpdate: (updates: Partial<CardInstance>) => void;
    onRemove: () => void;
}

// Premiere Pro style instance row
const InstanceRow: React.FC<InstanceRowProps> = ({
    instance,
    index,
    isSelected,
    onSelect,
    onUpdate,
    onRemove
}) => {
    return (
        <div
            onClick={onSelect}
            className={`
                flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer
                transition-colors
                ${isSelected
                    ? 'bg-slate-blue/10 border border-slate-blue/30'
                    : 'bg-onyx hover:bg-graphite border border-iron/40'
                }
            `}
        >
            <span className="text-[9px] text-gray-500 font-mono">#{index + 1}</span>
            <span className={`
                text-[10px] font-medium px-1.5 py-0.5 rounded
                ${instance.lane === 'macro'
                    ? 'bg-slate-blue/15 text-slate-blue-soft'
                    : 'bg-entity/15 text-entity-soft'
                }
            `}>
                {instance.lane === 'macro' ? 'Macro' : 'Micro'}
            </span>
            <span className="text-[10px] text-gray-400 flex-1">
                {Math.round(instance.position * 100)}%
            </span>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove();
                }}
                className="p-0.5 rounded text-gray-500 hover:text-red-400 transition-colors"
            >
                <X className="w-3 h-3" />
            </button>
        </div>
    );
};

// Premiere Pro style quick place button
interface QuickPlaceButtonProps {
    label: string;
    onClick: () => void;
}

const QuickPlaceButton: React.FC<QuickPlaceButtonProps> = ({ label, onClick }) => (
    <button
        onClick={onClick}
        className={`
            px-3 py-1 rounded text-[10px] font-medium
            transition-colors border
            ${label === 'Macro'
                ? 'bg-slate-blue/10 text-slate-blue-soft border-slate-blue/30 hover:bg-slate-blue/20'
                : 'bg-entity/10 text-entity-soft border-entity/30 hover:bg-entity/20'
            }
        `}
    >
        + {label}
    </button>
);
